import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useBreakpointValue,
    Badge,
    Icon,
    Divider,
    Progress,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import {
    FaSync,
    FaUsers,
    FaGlobeAmericas,
    FaChartLine,
    FaBrain,
    FaShieldAlt,
    FaUserFriends,
    FaChartPie,
    FaTachometerAlt,
    FaExclamationTriangle,
    FaCheckCircle,
    FaUserTie,
    FaCalendarAlt,
    FaBalanceScale,
    FaArrowUp,
    FaDownload
} from 'react-icons/fa';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    AreaChart,
    Area,
} from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import DashboardRoleBrief from '../../compnents/DashboardRoleBrief';
import RoleOperationalKpis from '../../compnents/RoleOperationalKpis';
import ProductDataSignalsPanel from '../../compnents/ProductDataSignalsPanel';
import DataReadinessStrip from '../../compnents/DataReadinessStrip';
import SyntheticPreviewBanner from '../../compnents/SyntheticPreviewBanner';
import { useNavigate } from 'react-router-dom';
import { demoHeadDashboard } from '../../content/demoAnalytics';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardHeadDashboardPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [downloadingPng, setDownloadingPng] = useState(false);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const toast = useToast();

    const isMobile = useBreakpointValue({ base: true, md: false });
    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#0B2545';
    const secondaryColor = themeData?.colors?.secondary || '#13315C';
    const accentColor = themeData?.colors?.accent || '#13C1BB';
    const backgroundColor = themeData?.colors?.background || '#F5F7FA';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast
    const headingTextColor = isDarkTheme ? '#F5F7FA' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.25)' : '#E2E8F0';

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#F6AD55' : '#DD6B20';
    const infoColor = isDarkTheme ? '#13C1BB' : '#3182CE';
    const purpleColor = isDarkTheme ? '#B794F4' : '#805AD5';
    const tealColor = isDarkTheme ? '#4FD1C5' : '#319795';

    // Info card backgrounds
    const successBg = isDarkTheme ? 'rgba(104, 211, 145, 0.15)' : '#D1FAE5';
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.15)' : '#FEE2E2';
    const warningBg = isDarkTheme ? 'rgba(246, 173, 85, 0.15)' : '#FED7AA';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';
    const purpleBg = isDarkTheme ? 'rgba(183, 148, 244, 0.15)' : '#E9D8FD';
    const tealBg = isDarkTheme ? 'rgba(79, 209, 197, 0.15)' : '#B2F5EA';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Chart axis color
    const axisColor = isDarkTheme ? bodyTextColor : '#4A5568';

    // Chart colors
    const chartPrimaryColor = isDarkTheme ? accentColor : primaryColor;

    // Color palettes
    const RISK_COLORS = isDarkTheme
        ? [successColor, warningColor, errorColor]
        : [successColor, warningColor, errorColor];

    const GEO_COLORS = isDarkTheme
        ? [accentColor, successColor, purpleColor, warningColor, tealColor, errorColor]
        : [primaryColor, successColor, '#805AD5', warningColor, '#319795', errorColor];

    const SEGMENT_COLORS = isDarkTheme
        ? [accentColor, successColor, purpleColor, warningColor, errorColor, tealColor, '#F687B3', '#FBD38D']
        : [primaryColor, successColor, '#805AD5', warningColor, errorColor, '#319795', '#D53F8C', '#D69E2E'];

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/content/head-dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDashboardData(res.data?.metrics ? res.data : demoHeadDashboard);
            setLastRefreshedAt(new Date());
        } catch (error) {
            console.error('Dashboard fetch error:', error);
            setDashboardData(demoHeadDashboard);
            setLastRefreshedAt(new Date());
        } finally {
            setLoading(false);
        }
    };

    const handleExportPng = async () => {
        try {
            setDownloadingPng(true);
            const response = await axios.get(`${API_BASE_URL}/content/export-png/head`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Important for file download
            });

            // Create a download link and trigger it
            const blob = new Blob([response.data], { type: 'image/png' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard_head_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: 'Success',
                description: 'Dashboard PNG snapshot exported. PDF and CSV packs are available in Reports.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            console.error('Export PNG error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to export dashboard.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setDownloadingPng(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line
    }, []);

    // Prepare geographic distribution data
    const getGeographicData = () => {
        if (!dashboardData?.metrics?.geographic_distribution) return [];
        const geo = dashboardData.metrics.geographic_distribution;
        return Object.entries(geo)
            .map(([country, count], index) => ({
                name: country,
                value: count,
                color: GEO_COLORS[index % GEO_COLORS.length],
            }))
            .sort((a, b) => b.value - a.value);
    };

    // Prepare risk tolerance data
    const getRiskToleranceData = () => {
        if (!dashboardData?.metrics?.risk_tolerance_scores) return [];
        const risk = dashboardData.metrics.risk_tolerance_scores;
        return [
            { name: 'Low Risk', value: risk.low, color: RISK_COLORS[0] },
            { name: 'Medium Risk', value: risk.medium, color: RISK_COLORS[1] },
            { name: 'High Risk', value: risk.high, color: RISK_COLORS[2] },
        ];
    };

    // Prepare age distribution data
    const getAgeDistributionData = () => {
        if (!dashboardData?.age_distribution) return [];
        return dashboardData.age_distribution.map(item => ({
            name: item.bucket,
            count: item.count,
            percentage: item.percentage,
        }));
    };

    // Prepare segments data
    const getSegmentsData = () => {
        if (!dashboardData?.segments) return [];
        return dashboardData.segments
            .sort((a, b) => b.count - a.count)
            .map((item, index) => ({
                name: item.segment,
                value: item.count,
                percentage: item.percentage,
                color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
            }));
    };

    // Prepare bias ranking data
    const getBiasRankingData = () => {
        if (!dashboardData?.bias_ranking) return [];
        return dashboardData.bias_ranking.slice(0, 5).map(bias => ({
            name: bias.bias_name.replace(/_/g, ' '),
            score: bias.mean_score,
            affected: bias.n,
        }));
    };

    // Prepare login frequency data
    const getLoginFrequencyData = () => {
        if (!dashboardData?.metrics?.seat_utilization?.login_frequency) return [];
        const freq = dashboardData.metrics.seat_utilization.login_frequency;
        return [
            { name: 'Daily', value: freq.daily, color: successColor },
            { name: 'Weekly', value: freq.weekly, color: warningColor },
            { name: 'Monthly', value: freq.monthly, color: infoColor },
        ];
    };

    // Prepare allocation drift data
    const getAllocationDriftData = () => {
        if (!dashboardData?.metrics?.allocation_drift_risk?.drift_by_asset_class) return [];
        const drift = dashboardData.metrics.allocation_drift_risk.drift_by_asset_class;
        return Object.entries(drift).map(([asset, value]) => ({
            name: asset.charAt(0).toUpperCase() + asset.slice(1),
            value: value,
        }));
    };

    const metrics = dashboardData?.metrics || {};
    const geoData = getGeographicData();
    const riskTolerance = getRiskToleranceData();
    const ageDistribution = getAgeDistributionData();
    const segments = getSegmentsData();
    const biasRanking = getBiasRankingData();
    const loginFrequency = getLoginFrequencyData();
    const allocationDrift = getAllocationDriftData();
    const lastUpdatedLabel = lastRefreshedAt ? lastRefreshedAt.toLocaleString() : null;
    const largestSegment = segments[0];
    const topBias = biasRanking[0];
    const dailyLogins = loginFrequency.find((item) => item.name === 'Daily')?.value || 0;
    const briefingHighlights = [
        {
            label: 'Largest segment',
            value: largestSegment?.name || 'n/a',
            help: largestSegment ? `${Number(largestSegment.percentage || 0).toFixed(1)}% of the active book` : 'Shown after the next successful refresh',
        },
        {
            label: 'Top behavioural pressure',
            value: topBias?.name || 'n/a',
            help: topBias ? `${Number(topBias.score || 0).toFixed(1)} mean score across the affected base` : 'Top bias ranking is currently unavailable',
        },
        {
            label: 'Daily platform usage',
            value: dailyLogins,
            help: 'Daily seat logins as a proxy for adoption',
        },
    ];
    const briefingFocusAreas = [
        'Read segment concentration before drilling into country or age detail',
        'Use the leading bias and drift views to steer advisory coaching priorities',
        'Refresh before executive reviews so seat usage and trend signals stay current',
    ];

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    <DataReadinessStrip role="Head of Advisory" />
                    {dashboardData?.isSyntheticDemo ? (
                        <SyntheticPreviewBanner message="Live tenant metrics are not available yet, so this dashboard is showing a synthetic demo sample. Values will switch to firm data once scoring metrics are returned for this tenant." />
                    ) : null}
                    {/* Header */}
                    <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
                        <Box flex="1">
                            <Heading
                                size={{ base: 'lg', md: 'xl' }}
                                color={headingTextColor}
                                mb={2}
                                fontWeight="bold"
                            >
                                <HStack spacing={3}>
                                    <Icon as={FaUserTie} boxSize={{ base: 6, md: 8 }} />
                                    <Text>HoA Dashboard</Text>
                                </HStack>
                            </Heading>
                            <Text color={bodyTextColor} fontSize="sm">
                                Comprehensive overview of client portfolio, risk metrics, and business intelligence
                            </Text>
                        </Box>
                        <HStack spacing={2}>
                            <Button
                                leftIcon={<FaDownload />}
                                bg={accentColor}
                                color="white"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={handleExportPng}
                                isLoading={downloadingPng}
                                _hover={{ bg: isDarkTheme ? secondaryColor : accentColor }}
                            >
                                Export PNG snapshot
                            </Button>
                            <Button
                                leftIcon={<FaDownload />}
                                variant="outline"
                                colorScheme="blue"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={() => navigate('/reportsExports')}
                            >
                                PDF / CSV reports
                            </Button>
                            <Button
                                leftIcon={<FaSync />}
                                bg={primaryColor}
                                color="white"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={fetchDashboardData}
                                isLoading={loading}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Refresh
                            </Button>
                        </HStack>
                    </Flex>

                    {loading && (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor}>Loading dashboard...</Text>
                            </VStack>
                        </Flex>
                    )}

                    {!loading && dashboardData && (
                        <>
                            <DashboardRoleBrief
                                title="Keep the head-of-advisory view strategic and fast to read"
                                description="This page should summarize where the advisory book is concentrated, which behavioural signals matter most, and whether platform adoption supports action at scale."
                                highlights={briefingHighlights}
                                focusAreas={briefingFocusAreas}
                                lastUpdatedLabel={lastUpdatedLabel}
                                bg={cardBg}
                                borderColor={borderColor}
                                titleColor={headingTextColor}
                                descriptionColor={bodyTextColor}
                                accentColor={isDarkTheme ? accentColor : primaryColor}
                                cardBg={isDarkTheme ? 'rgba(45, 55, 72, 0.45)' : 'gray.50'}
                            />

                            <RoleOperationalKpis
                                role="head"
                                bg={cardBg}
                                borderColor={borderColor}
                                titleColor={headingTextColor}
                                descriptionColor={bodyTextColor}
                                accentColor={isDarkTheme ? accentColor : primaryColor}
                                cardBg={isDarkTheme ? 'rgba(45, 55, 72, 0.45)' : 'gray.50'}
                            />

                            {/* Summary Cards */}
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Active Clients
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                                {metrics.total_active_clients?.toLocaleString()}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Total portfolio
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                {/* Tenure only exists in the synthetic demo payload — the ingest
                                    does not capture account open dates yet. */}
                                {metrics.average_account_tenure_days !== undefined && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaCalendarAlt} color={successColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Avg Tenure
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={successColor} fontWeight="bold">
                                                {Math.round(metrics.average_account_tenure_days / 30)} mo
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                {metrics.average_account_tenure_days} days
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                )}

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaShieldAlt} color={warningColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Avg Risk Profile
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={warningColor} fontWeight="bold">
                                                {metrics.executive_level_insights?.average_risk_profile?.toFixed(1)}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Out of 10
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaCheckCircle} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    With Scores
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                                {metrics.executive_level_insights?.customers_with_scores?.toLocaleString()}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                {((metrics.executive_level_insights?.customers_with_scores / metrics.total_active_clients) * 100).toFixed(1)}% coverage
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Bias Mitigation Progress — mitigation trend tracking needs score
                                history; the values only exist in the synthetic demo payload. */}
                            {metrics.clients_with_bias_mitigated_pct && (
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Bias Mitigation Progress
                                        </Heading>
                                    </HStack>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                        <Card bg={successBg} borderRadius="lg" boxShadow="sm" borderWidth="2px" borderColor={successColor}>
                                            <CardBody>
                                                <VStack spacing={3}>
                                                    <HStack justify="space-between" w="100%">
                                                        <Text fontSize="sm" fontWeight="bold" color={labelTextColor}>
                                                            30-Day Mitigation Rate
                                                        </Text>
                                                        <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                                                            <HStack spacing={1}>
                                                                <Icon as={FaArrowUp} boxSize={3} />
                                                                <Text>{metrics.clients_with_bias_mitigated_pct?.['30d']}%</Text>
                                                            </HStack>
                                                        </Badge>
                                                    </HStack>
                                                    <Progress
                                                        value={metrics.clients_with_bias_mitigated_pct?.['30d']}
                                                        w="100%"
                                                        h="12px"
                                                        borderRadius="full"
                                                        colorScheme="green"
                                                    />
                                                    <Text fontSize="xs" color={bodyTextColor} alignSelf="start">
                                                        Clients showing improvement in last 30 days
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        <Card bg={infoBg} borderRadius="lg" boxShadow="sm" borderWidth="2px" borderColor={infoColor}>
                                            <CardBody>
                                                <VStack spacing={3}>
                                                    <HStack justify="space-between" w="100%">
                                                        <Text fontSize="sm" fontWeight="bold" color={labelTextColor}>
                                                            90-Day Mitigation Rate
                                                        </Text>
                                                        <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                                            <HStack spacing={1}>
                                                                <Icon as={FaArrowUp} boxSize={3} />
                                                                <Text>{metrics.clients_with_bias_mitigated_pct?.['90d']}%</Text>
                                                            </HStack>
                                                        </Badge>
                                                    </HStack>
                                                    <Progress
                                                        value={metrics.clients_with_bias_mitigated_pct?.['90d']}
                                                        w="100%"
                                                        h="12px"
                                                        borderRadius="full"
                                                        colorScheme="blue"
                                                    />
                                                    <Text fontSize="xs" color={bodyTextColor} alignSelf="start">
                                                        Clients showing improvement in last 90 days
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>
                                </CardBody>
                            </Card>
                            )}

                            {/* Charts Row 1: Geographic & Risk */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                {/* Geographic Distribution */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaGlobeAmericas} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Geographic Distribution
                                            </Heading>
                                        </HStack>
                                        {geoData.length > 0 ? (
                                            <Box>
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <BarChart data={geoData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                        <XAxis dataKey="name" stroke={axisColor} tick={{ fill: axisColor }} />
                                                        <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                        <RechartsTooltip
                                                            contentStyle={{
                                                                backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                                border: `1px solid ${borderColor}`,
                                                                borderRadius: '8px',
                                                                color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                            }}
                                                            formatter={(value) => [`${value} clients`, 'Count']}
                                                        />
                                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                            {geoData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                                <Wrap spacing={2} mt={3} justify="center">
                                                    {geoData.map((geo, index) => (
                                                        <WrapItem key={index}>
                                                            <Badge
                                                                px={3}
                                                                py={1}
                                                                borderRadius="full"
                                                                bg={geo.color}
                                                                color="white"
                                                                fontSize="xs"
                                                            >
                                                                {geo.name}: {geo.value}
                                                            </Badge>
                                                        </WrapItem>
                                                    ))}
                                                </Wrap>
                                            </Box>
                                        ) : (
                                            <Flex justify="center" align="center" h="250px">
                                                <Text color={mutedTextColor}>No data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                {/* Risk Tolerance Distribution */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBalanceScale} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Risk Tolerance Distribution
                                            </Heading>
                                        </HStack>
                                        {riskTolerance.length > 0 ? (
                                            <Box>
                                                <ResponsiveContainer width="100%" height={200}>
                                                    <PieChart>
                                                        <Pie
                                                            data={riskTolerance}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, percent }) =>
                                                                `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`
                                                            }
                                                            outerRadius={80}
                                                            dataKey="value"
                                                        >
                                                            {riskTolerance.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip
                                                            contentStyle={{
                                                                backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                                border: `1px solid ${borderColor}`,
                                                                borderRadius: '8px',
                                                                color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                            }}
                                                            formatter={(value) => [`${value} clients`, 'Count']}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <SimpleGrid columns={3} spacing={2} mt={3}>
                                                    {riskTolerance.map((risk, index) => (
                                                        <Card key={index} bg={index === 0 ? successBg : index === 1 ? warningBg : errorBg} borderRadius="md" boxShadow="sm">
                                                            <CardBody p={3} textAlign="center">
                                                                <Text fontSize="xs" color={bodyTextColor} mb={1}>{risk.name}</Text>
                                                                <Text fontSize="xl" fontWeight="bold" color={risk.color}>
                                                                    {risk.value}
                                                                </Text>
                                                            </CardBody>
                                                        </Card>
                                                    ))}
                                                </SimpleGrid>
                                            </Box>
                                        ) : (
                                            <Flex justify="center" align="center" h="250px">
                                                <Text color={mutedTextColor}>No data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Top Behavioral Biases */}
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Top 5 Behavioral Biases
                                        </Heading>
                                    </HStack>
                                    {biasRanking.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={biasRanking} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 20 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                <XAxis type="number" stroke={axisColor} tick={{ fill: axisColor }} />
                                                <YAxis
                                                    type="category"
                                                    dataKey="name"
                                                    stroke={axisColor}
                                                    tick={{ fill: axisColor, fontSize: isMobile ? 10 : 12 }}
                                                    width={90}
                                                />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                        border: `1px solid ${borderColor}`,
                                                        borderRadius: '8px',
                                                        color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                    }}
                                                    formatter={(value, name, props) => [
                                                        `Score: ${value}, Affected: ${props.payload.affected} clients`,
                                                        'Severity'
                                                    ]}
                                                />
                                                <Bar dataKey="score" fill={chartPrimaryColor} radius={[0, 8, 8, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Flex justify="center" align="center" h="300px">
                                            <Text color={mutedTextColor}>No data available</Text>
                                        </Flex>
                                    )}
                                </CardBody>
                            </Card>

                            {/* Age Distribution & Segments */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                {/* Age Distribution */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaUserFriends} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Age Distribution
                                            </Heading>
                                        </HStack>
                                        {ageDistribution.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={280}>
                                                <AreaChart data={ageDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                    <XAxis dataKey="name" stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                        formatter={(value, name, props) => [
                                                            `${value} clients (${props.payload.percentage?.toFixed(1)}%)`,
                                                            'Count'
                                                        ]}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="count"
                                                        stroke={chartPrimaryColor}
                                                        fill={chartPrimaryColor}
                                                        fillOpacity={0.6}
                                                        strokeWidth={2}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="280px">
                                                <Text color={mutedTextColor}>No data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                {/* Customer Segments */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaChartPie} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Customer Segments
                                            </Heading>
                                        </HStack>
                                        {segments.length > 0 ? (
                                            <VStack spacing={3} align="stretch" maxH="280px" overflowY="auto" pr={2}>
                                                {segments.map((segment, index) => (
                                                    <Card key={index} bg={infoBg} borderRadius="md" boxShadow="sm">
                                                        <CardBody p={3}>
                                                            <VStack spacing={2} align="stretch">
                                                                <HStack justify="space-between">
                                                                    <Text fontSize="sm" fontWeight="bold" color={labelTextColor} noOfLines={1}>
                                                                        {segment.name}
                                                                    </Text>
                                                                    <Badge
                                                                        bg={segment.color}
                                                                        color="white"
                                                                        fontSize="xs"
                                                                        px={2}
                                                                        py={0.5}
                                                                    >
                                                                        {segment.percentage.toFixed(1)}%
                                                                    </Badge>
                                                                </HStack>
                                                                <HStack justify="space-between">
                                                                    <Progress
                                                                        value={segment.percentage}
                                                                        flex={1}
                                                                        h="6px"
                                                                        borderRadius="full"
                                                                        sx={{
                                                                            '& > div': {
                                                                                backgroundColor: segment.color,
                                                                            },
                                                                        }}
                                                                    />
                                                                    <Text fontSize="sm" fontWeight="bold" color={labelTextColor} minW="50px" textAlign="right">
                                                                        {segment.value}
                                                                    </Text>
                                                                </HStack>
                                                            </VStack>
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </VStack>
                                        ) : (
                                            <Flex justify="center" align="center" h="280px">
                                                <Text color={mutedTextColor}>No data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Seat Utilization & Allocation Drift */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                                {/* Seat Utilization */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    {/* Seat/usage telemetry only exists in the synthetic demo payload. */}
                                    {metrics.seat_utilization ? (
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaTachometerAlt} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Platform Utilization
                                            </Heading>
                                        </HStack>
                                        <VStack spacing={4} align="stretch">
                                            <SimpleGrid columns={2} spacing={3}>
                                                <Card bg={successBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3}>
                                                        <Stat size="sm">
                                                            <StatLabel color={bodyTextColor}>7-Day Active</StatLabel>
                                                            <StatNumber color={successColor}>
                                                                {metrics.seat_utilization?.active_users_7d?.toLocaleString()}
                                                            </StatNumber>
                                                        </Stat>
                                                    </CardBody>
                                                </Card>
                                                <Card bg={infoBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3}>
                                                        <Stat size="sm">
                                                            <StatLabel color={bodyTextColor}>30-Day Active</StatLabel>
                                                            <StatNumber color={infoColor}>
                                                                {metrics.seat_utilization?.active_users_30d?.toLocaleString()}
                                                            </StatNumber>
                                                        </Stat>
                                                    </CardBody>
                                                </Card>
                                            </SimpleGrid>
                                            <Divider borderColor={borderColor} />
                                            {loginFrequency.length > 0 && (
                                                <ResponsiveContainer width="100%" height={150}>
                                                    <BarChart data={loginFrequency} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                        <XAxis dataKey="name" stroke={axisColor} tick={{ fill: axisColor, fontSize: 11 }} />
                                                        <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                        <RechartsTooltip
                                                            contentStyle={{
                                                                backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                                border: `1px solid ${borderColor}`,
                                                                borderRadius: '8px',
                                                                color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                            }}
                                                            formatter={(value) => [`${value} users`, 'Frequency']}
                                                        />
                                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                            {loginFrequency.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </VStack>
                                    </CardBody>
                                    ) : (
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaTachometerAlt} color={mutedTextColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Platform Utilization
                                            </Heading>
                                        </HStack>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                            Seat and usage telemetry is not collected for this tenant yet. This card activates when platform usage tracking ships.
                                        </Text>
                                    </CardBody>
                                    )}
                                </Card>

                                {/* Allocation Drift Risk — computed live from holdings vs model bands */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    {metrics.allocation_drift_risk ? (
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaExclamationTriangle} color={warningColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Allocation Drift Risk
                                            </Heading>
                                        </HStack>
                                        <VStack spacing={4} align="stretch">
                                            <Card bg={warningBg} borderRadius="md" boxShadow="sm" borderWidth="2px" borderColor={warningColor}>
                                                <CardBody>
                                                    <SimpleGrid columns={2} spacing={3}>
                                                        <VStack align="start" spacing={1}>
                                                            <Text fontSize="xs" color={bodyTextColor}>Clients Outside Bands</Text>
                                                            <Text fontSize="2xl" fontWeight="bold" color={warningColor}>
                                                                {metrics.allocation_drift_risk?.clients_outside_bands}
                                                            </Text>
                                                        </VStack>
                                                        <VStack align="start" spacing={1}>
                                                            <Text fontSize="xs" color={bodyTextColor}>% of Book</Text>
                                                            <Text fontSize="2xl" fontWeight="bold" color={warningColor}>
                                                                {metrics.allocation_drift_risk?.pct_of_book}%
                                                            </Text>
                                                        </VStack>
                                                        {metrics.allocation_drift_risk?.drift_cases !== undefined && (
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontSize="xs" color={bodyTextColor}>Open Drift Cases</Text>
                                                                <Text fontSize="2xl" fontWeight="bold" color={warningColor}>
                                                                    {metrics.allocation_drift_risk?.drift_cases}
                                                                </Text>
                                                            </VStack>
                                                        )}
                                                        {metrics.allocation_drift_risk?.worst_drift_pp !== undefined && (
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontSize="xs" color={bodyTextColor}>Worst Drift</Text>
                                                                <Text fontSize="2xl" fontWeight="bold" color={warningColor}>
                                                                    {metrics.allocation_drift_risk?.worst_drift_pp}pp
                                                                </Text>
                                                            </VStack>
                                                        )}
                                                    </SimpleGrid>
                                                </CardBody>
                                            </Card>
                                            {allocationDrift.length > 0 && (
                                                <ResponsiveContainer width="100%" height={150}>
                                                    <BarChart data={allocationDrift} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                        <XAxis
                                                            dataKey="name"
                                                            stroke={axisColor}
                                                            tick={{ fill: axisColor, fontSize: 10 }}
                                                            angle={-20}
                                                            textAnchor="end"
                                                            height={60}
                                                        />
                                                        <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                        <RechartsTooltip
                                                            contentStyle={{
                                                                backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                                border: `1px solid ${borderColor}`,
                                                                borderRadius: '8px',
                                                                color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                            }}
                                                            formatter={(value) => [`${value}%`, 'Drift']}
                                                        />
                                                        <Bar dataKey="value" fill={warningColor} radius={[8, 8, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </VStack>
                                    </CardBody>
                                    ) : (
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaExclamationTriangle} color={mutedTextColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Allocation Drift Risk
                                            </Heading>
                                        </HStack>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                            Drift is computed from customer holdings and model portfolio targets. Upload both firm-ready datasets to activate this card.
                                        </Text>
                                    </CardBody>
                                    )}
                                </Card>
                            </SimpleGrid>

                            {/* Feature Usage Metrics — demo-only telemetry */}
                            {metrics.seat_utilization?.feature_usage && (
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaChartLine} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Feature Usage Metrics
                                        </Heading>
                                    </HStack>
                                    <SimpleGrid columns={{ base: 3, md: 3 }} spacing={4}>
                                        <Card bg={infoBg} borderRadius="md" boxShadow="sm">
                                            <CardBody p={4} textAlign="center">
                                                <VStack spacing={2}>
                                                    <Icon as={FaChartPie} boxSize={8} color={infoColor} />
                                                    <Text fontSize="xs" color={bodyTextColor}>Dashboard Views</Text>
                                                    <Text fontSize="2xl" fontWeight="bold" color={infoColor}>
                                                        {metrics.seat_utilization?.feature_usage?.dashboard_views?.toLocaleString()}
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        <Card bg={tealBg} borderRadius="md" boxShadow="sm">
                                            <CardBody p={4} textAlign="center">
                                                <VStack spacing={2}>
                                                    <Icon as={FaChartLine} boxSize={8} color={tealColor} />
                                                    <Text fontSize="xs" color={bodyTextColor}>Report Downloads</Text>
                                                    <Text fontSize="2xl" fontWeight="bold" color={tealColor}>
                                                        {metrics.seat_utilization?.feature_usage?.report_downloads?.toLocaleString()}
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        <Card bg={purpleBg} borderRadius="md" boxShadow="sm">
                                            <CardBody p={4} textAlign="center">
                                                <VStack spacing={2}>
                                                    <Icon as={FaCheckCircle} boxSize={8} color={purpleColor} />
                                                    <Text fontSize="xs" color={bodyTextColor}>Recommendations Accepted</Text>
                                                    <Text fontSize="2xl" fontWeight="bold" color={purpleColor}>
                                                        {metrics.seat_utilization?.feature_usage?.recommendations_accepted?.toLocaleString()}
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>
                                </CardBody>
                            </Card>
                            )}
                        </>
                    )}

                    <ProductDataSignalsPanel
                        roleFilter={['Portfolio Adviser', 'Compliance']}
                        title="Live firm signals across portfolio and compliance queues"
                        helperText="Aggregates live drift, evidence and risk-mismatch signals from the tenant product layer. Use the role-specific tabs in Portfolio Adviser and Compliance for the drilldown queues."
                        emptyMessage="No live portfolio or compliance signals against the tenant catalog this week."
                    />

                    {!loading && !dashboardData && (
                        <EmptyStateCard
                            icon={FaUserTie}
                            title="Head-of-advisory analytics are temporarily unavailable"
                            description="This strategic dashboard could not load its latest portfolio and adoption summary."
                            hint="Try a refresh now. If the issue persists, confirm the most recent dataset is complete and the head dashboard endpoint is healthy."
                            bg={cardBg}
                            borderColor={borderColor}
                            titleColor={headingTextColor}
                            descriptionColor={bodyTextColor}
                            hintColor={mutedTextColor}
                            iconColor={isDarkTheme ? accentColor : primaryColor}
                            maxW="4xl"
                        >
                            <Button
                                leftIcon={<FaSync />}
                                bg={primaryColor}
                                color="white"
                                size="sm"
                                onClick={fetchDashboardData}
                                isLoading={loading}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Retry dashboard load
                            </Button>
                        </EmptyStateCard>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardHeadDashboardPaid;
